import React, { useEffect, useRef } from "react";
import { SearchInput, SearchInputProps } from "../SearchInput/SearchInput";
import { ISearchTermParameters, StringIndexedDict } from "../../../api/GenericTypes";
import { CheckboxRound } from "../../formfields/CheckboxFormField/CheckboxFormField";
import { toUppercase } from "../../helperfunctions/stringFunctions";
import { OverlayInfo } from "../../misc/OverlayInfo/OverlayInfo";

const searchTermParamPrefix = "searchTermInclude";
export const searchTermMinLength = 3;
interface SearchInputWithOptionsProps<
  Filters extends StringIndexedDict & ISearchTermParameters,
> extends SearchInputProps {
  searchTermOptions: { [key in keyof Filters]: string };
  filters: Filters;
  setFilters: (newFilters: Filters) => void;
}
const SearchInputWithOptions = <Filters extends StringIndexedDict & ISearchTermParameters>({
  placeholder = "Search",
  searchValue,
  setSearchValue,
  size,
  children,
  minimized = false,
  minimizedBtnCls,
  minimizedDisabled = false,
  title,
  error,
  disabled,
  disableDefaultError = true,
  searchTermOptions,
  filters,
  setFilters,
}: SearchInputWithOptionsProps<Filters>) => {
  const hasInitChecked = useRef(false);
  const activeFilters = Object.entries(filters)
    .filter(([key, value]) => key.startsWith(searchTermParamPrefix) && value)
    .map(([key, _]) => searchTermOptions[key]);

  useEffect(() => {
    if (!hasInitChecked.current) {
      if (activeFilters.length === 0) {
        setFilters({
          ...filters,
          searchTermIncludeNames: true,
        });
      }
      hasInitChecked.current = true;
    }
  }, [activeFilters.length, filters, setFilters]);

  // useEffect(() => {
  //   if (!tabsLoading) {
  //     console.log("Updating filters based on activeFilters", activeFilters, filters);
  //     setFilters({
  //       ...filters,
  //       ...Object.fromEntries(
  //         Object.keys(searchTermOptions).map((key) => [key, (activeFilters ?? []).includes(searchTermOptions[key])])
  //       ),
  //     });
  //   }
  // }, [activeFilters, filters, searchTermOptions, setFilters, tabsLoading]);

  const showMinLengthError = disableDefaultError
    ? false
    : filters.searchTerm && filters.searchTerm.length < searchTermMinLength && !filters.searchTermIncludeIds;
  const showMinCharError = disableDefaultError
    ? false
    : filters.searchTerm && filters.searchTerm.length < searchTermMinLength && filters.searchTermIncludeIds;
  return (
    <div className="flex col-nowrap" style={{ gap: "2.5px", width: "100%" }}>
      <SearchInput
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        size={size}
        minimized={minimized}
        minimizedBtnCls={minimizedBtnCls}
        minimizedDisabled={minimizedDisabled}
        title={title}
        // size="sm"
        placeholder={
          Array.isArray(activeFilters) && !!activeFilters.length
            ? `Search in ${activeFilters.map((f) => toUppercase(f)).join(", ")}...`
            : placeholder
        }
        error={
          showMinLengthError
            ? `Please enter at least ${searchTermMinLength} characters...`
            : showMinCharError
              ? `Please enter at least ${searchTermMinLength} characters to include text based searching...`
              : error
        }
        disabled={disabled || !activeFilters.length}
        disableDefaultError
      >
        {children}
      </SearchInput>
      <div className="flex row-nowrap gap-5" style={{ overflow: "hidden", fontSize: "1rem" }}>
        <span style={{ color: "gray" }}>Include:</span>
        {Object.entries(searchTermOptions).map(([searchOption, humanReadableOption], i) => (
          <div className="flex row-nowrap align-center" style={{ gap: "2.5px", overflow: "hidden" }} key={i}>
            <CheckboxRound
              checked={!!filters?.[searchOption]}
              onChange={(_, checked) =>
                setFilters({
                  ...filters,
                  [searchOption]: checked,
                })
              }
              disabled={!!filters?.[searchOption] && activeFilters.length === 1}
            />
            <div className="ellipsisContainer">
              <span
                style={{
                  color: "gray",
                }}
              >
                {searchOption === "searchTermIncludeCustomFieldValues" ? (
                  <>
                    {toUppercase(humanReadableOption)}{" "}
                    <OverlayInfo icon="info">
                      Only custom fields that contain plain text or lists of text can be searched
                    </OverlayInfo>
                  </>
                ) : (
                  toUppercase(humanReadableOption)
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default SearchInputWithOptions;
